import React, { useEffect, useRef, useState } from 'react';
interface Props {
  url: string;
  width: number;
  height: number;
  scale: number;
  fontScale: number;
  placeholder: string; // should be data:image encoded URL string
  showPlaceholder: boolean;
  exporting: boolean;
  onClick: ({
    url,
    newTab,
    useBottomDrawer,
  }: {
    url?: string;
    newTab?: boolean;
    useBottomDrawer?: boolean;
  }) => void;
  onLoaded: () => void;
}

const EmbeddedExternalContentContainer: React.FC<Props> = ({
  url,
  width,
  height,
  scale,
  fontScale,
  placeholder,
  showPlaceholder,
  exporting,
  onClick,
  onLoaded,
}) => {
  const [visible, setVisible] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const showSpinner = !visible && !showPlaceholder;
  const imageSize = Math.min(width, height);

  // Scale the svg against the width. This ensures that the svg maintains roughly
  // the same padding, even with very tall+skinny or short+wide objects. Arbitrarily
  // increase the size by a scale factor of 25 to make it more visible.
  const imageScale = (imageSize / width) * 25;

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    // Store the click handler so we can remove it later
    const handleIframeClick = function (e: MouseEvent) {
      const target = e.target as HTMLElement;
      if (target) {
        const url =
          target.getAttribute('data-drawer-url') ||
          target.getAttribute('data-bottom-drawer-url') ||
          target.getAttribute('href');
        const isCtrlKeyHeld = e.ctrlKey || e.metaKey;
        const inBottomDrawer = target.hasAttribute('data-bottom-drawer-url');
        const inDrawer = target.hasAttribute('data-drawer-url');
        const newTab = !(inDrawer || inBottomDrawer) || isCtrlKeyHeld;
        // Only open in bottom drawer if not opening in a new tab or the
        // regular drawer
        const useBottomDrawer = !newTab && !inDrawer && inBottomDrawer;

        if (url) {
          e.preventDefault();
          e.stopPropagation();
          onClick({
            url,
            newTab,
            useBottomDrawer, // prefer side drawer over bottom drawer
          });
        } else {
          onClick({});
        }
      } else {
        onClick({});
      }
    };

    const handleLoad = function () {
      try {
        const iframeContent = iframe.contentWindow;
        if (iframeContent?.document) {
          iframeContent.addEventListener('click', handleIframeClick);
        }
      } catch (error) {
        console.error('Error setting up iframe click handler:', error);
      }
    };

    // Add load event listener
    iframe.addEventListener('load', handleLoad);

    // Cleanup function to remove event listeners when component unmounts
    return () => {
      try {
        iframe.removeEventListener('load', handleLoad);

        const iframeContent = iframe.contentWindow;
        if (iframeContent?.document) {
          iframeContent.removeEventListener('click', handleIframeClick);
        }
      } catch (error) {
        // Ignore errors during cleanup
        console.debug('Cleanup error in EmbeddedBookmarkContainer:', error);
      }
    };
  }, [onClick]);

  return (
    <div
      style={{
        position: 'absolute',
        width: width * scale,
        height: height * scale,
        overflow: 'hidden',
        backgroundColor: 'var(--theme-primary-background)',
      }}
    >
      {showSpinner && (
        <aha-spinner
          size={`${imageSize / 10}px`}
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      )}
      {showPlaceholder ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <img
            src={placeholder}
            style={{
              ...(exporting
                ? { width: '100%', height: '100%' }
                : { width: `${imageScale}%` }),
            }}
          />
          {!exporting && (
            <span
              style={{
                fontSize: `${fontScale}em`,
                color: '#B5B5B5', // matches svg fill color
              }}
            >
              Preview is unavailable
            </span>
          )}
        </div>
      ) : (
        <iframe
          ref={iframeRef}
          src={url}
          frameBorder='0'
          seamless
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            width,
            height,
            transformOrigin: 'left top',
            transform: `scale(${scale})`,
            display: visible ? 'block' : 'none',
          }}
          onLoad={() => {
            setVisible(true);
            onLoaded();
          }}
        />
      )}
    </div>
  );
};

export default EmbeddedExternalContentContainer;
